import { useEffect, useState } from "react";
import * as Scrivito from "scrivito";
import moment from "moment";
import Carousel from "react-bootstrap/Carousel";
import { Event } from "^/Objs/EventPage/EventList";
import I18n from "^/config/i18n";

const BREAKPOINT_DESKTOP = 1200;
const BREAKPOINT_MOBILE = 768;

export function itemsPerSlideForWidth(width) {
  if (width < BREAKPOINT_MOBILE) return 1;
  if (width < BREAKPOINT_DESKTOP) return 3;
  return 5;
}

function useItemsPerSlide() {
  const [itemsPerSlide, setItemsPerSlide] = useState(() =>
    typeof window === "undefined" ? 5 : itemsPerSlideForWidth(window.innerWidth)
  );

  useEffect(() => {
    const handleResize = () =>
      setItemsPerSlide(itemsPerSlideForWidth(window.innerWidth));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return itemsPerSlide;
}

export function chunk(list, size) {
  const chunks = [];
  for (let i = 0; i < list.length; i += size) {
    chunks.push(list.slice(i, i + size));
  }
  return chunks;
}

export function resolveCarouselInterval(slideInterval, shouldPause) {
  if (shouldPause || slideInterval === 0) return null;
  return slideInterval;
}

export const EventsSliderWidgetComponent = ({ widget }) => {
  const siteId = widget.obj().siteId();
  const eventCount = widget.get("eventCount") || 10;
  const slideInterval = widget.get("slideInterval") ?? 5000;
  const category = widget.get("category");
  const eventLink = widget.get("eventLink");
  const currentHour = moment().startOf("day").toDate();

  let eventsQuery = Scrivito.Obj.onSite(siteId)
    .where("_objClass", "equals", "EventPage")
    .andNot("endDate", "isLessThan", currentHour);

  if (category) {
    eventsQuery = eventsQuery.and("category", "equals", category);
  }

  const events = eventsQuery.order("date", "asc").take(eventCount);

  const itemsPerSlide = useItemsPerSlide();
  const slides = chunk(events, itemsPerSlide);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (index >= slides.length) {
      setIndex(0);
    }
  }, [index, slides.length]);

  if (slides.length === 0) {
    return null;
  }

  const shouldPause = Scrivito.isInPlaceEditingActive();

  return (
    <div className="events-slider">
      <Carousel
        activeIndex={index}
        onSelect={setIndex}
        controls={slides.length > 1}
        indicators={slides.length > 1}
        pause="hover"
        interval={resolveCarouselInterval(slideInterval, shouldPause)}
        slide={true}
        prevIcon={<i className="las la-angle-left" aria-hidden="true" />}
        nextIcon={<i className="las la-angle-right" aria-hidden="true" />}
      >
        {slides.map((slideEvents, slideIndex) => (
          <Carousel.Item key={slideIndex}>
            <div className="events-slider__row">
              {slideEvents.map((event) => (
                <div className="events-slider__item" key={event.id()}>
                  <Event event={event} className="--inList h-100 bg-white" />
                </div>
              ))}
              {Array.from({
                length: itemsPerSlide - slideEvents.length,
              }).map((_, placeholderIndex) => (
                <div
                  className="events-slider__item events-slider__item--placeholder"
                  aria-hidden="true"
                  key={`placeholder-${placeholderIndex}`}
                />
              ))}
            </div>
          </Carousel.Item>
        ))}
      </Carousel>
      {eventLink && (
        <Scrivito.LinkTag
          to={eventLink}
          className="btn bg-yellow events-slider__all-events-link mt-3"
        >
          {eventLink.title() || I18n.t("Event all events")}
        </Scrivito.LinkTag>
      )}
    </div>
  );
};

Scrivito.provideComponent(
  "EventsSliderWidget",
  Scrivito.connect(EventsSliderWidgetComponent)
);
